/**
 * Design tokens — single source of truth for typography and text colors.
 *
 * TYPOGRAPHY
 * ----------
 * Hard floor: 12 px. Nothing below caption (12) is used for rendered text.
 * Base/body reading size: 16 px.
 *
 * TEXT COLORS
 * -----------
 * All semantic text colors are verified WCAG AA (≥ 4.5:1) on the navy
 * backgrounds used across the site:
 *   ground   #05090c  (~0.002 luminance)
 *   panel    #0c171a  (#091215 / #0b1a20 similar)
 *
 * Contrast notes (contrast ratio against #05090c / #0c171a):
 *   text.strong  #f2e8cf   16.4 / 14.6  PASS
 *   text.default #cdd6cf   13.4 / 11.9  PASS
 *   text.muted   #7a8e87    5.8 /  5.2  PASS
 *   gold.text    #ecd28b   13.5 / 12.0  PASS
 *   teal.text    #6fd3c4   11.2 /  9.9  PASS
 *
 * DECORATIVE values (borders, shadows, backgrounds) stay as inline literals
 * in the components — only text font-size + text color are tokenized here.
 */

// ── Type scale (px) ──────────────────────────────────────────────────────────

export const fontSize = {
  /** 12 px — hard floor. Labels, eyebrows, micro UI, nav links */
  caption: 12,
  /** 13 px — compact secondary text, tags, small metadata */
  small: 13,
  /** 14 px — button labels, compact body */
  body14: 14,
  /** 15 px — slightly elevated body copy */
  body15: 15,
  /** 16 px — base body reading size */
  body: 16,
  /** 18 px — lead / intro text */
  lead: 18,
  /** 20 px — h6 level, card titles */
  h6: 20,
  /** 24 px — h5 */
  h5: 24,
  /** 28 px — h4 */
  h4: 28,
  /** 36 px — h3 */
  h3: 36,
  /** 48 px — h2 */
  h2: 48,
  /** 64 px — display / hero */
  display: 64,
} as const satisfies Record<string, number>;

// ── Font weights ─────────────────────────────────────────────────────────────

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const satisfies Record<string, number>;

// ── Semantic text colors ─────────────────────────────────────────────────────

export const textColor = {
  /** High-contrast body copy and headings — cream white */
  strong: '#f2e8cf',
  /** Default readable text — muted warm grey-green */
  default: '#cdd6cf',
  /**
   * Muted / secondary text.
   * Minimum contrast 4.5:1 on all navy panel backgrounds.
   * Use instead of rgba(gold,.5) or very dark greens like #4e5e57.
   */
  muted: '#7a8e87',
  /** Gold accent text (#ecd28b) — high contrast, AA on all panels */
  gold: '#ecd28b',
  /** Teal accent text — AA on all panels */
  teal: '#6fd3c4',
} as const satisfies Record<string, string>;
